/**
 * <interactive-demo> – A custom element for guiding users through
 * step-by-step interactive demos on a page.
 *
 * Features:
 * - Positions itself relative to a target element.
 * - Supports multiple steps per demo sequence.
 * - Provides navigation controls (Next, Cancel, Close).
 * - Marks demos as completed using localStorage flag to prevent repeat display.
 *
 * Usage Example:
 *
 * ```liquid
 * <interactive-demo
 *   data-demo-name="save-search"      <!-- Required: unique demo identifier -->
 *   data-demo-step="1"                <!-- Required: step number in sequence -->
 *   data-target-id="fp-save-search-button" <!-- Required: target element ID -->
 *   data-position="right"             <!-- Optional: desktop position (default: placed flush left) -->
 *   data-mobile-position="left"       <!-- Optional: mobile position (defaults to data-position) -->
 * >
 *   <div class="h6 fp-font-weight--semibold">New! Save Search</div>
 *   <p class="body-md">Save your searches.</p>
 * </interactive-demo>
 * ```
 */
class InteractiveDemo extends HTMLElement {
  constructor() {
    super();
    this.close = this.close.bind(this);
    this.next = this.next.bind(this);
    this.open = this.open.bind(this);
    this.complete = this.complete.bind(this);
    this.positionDemo = this.positionDemo.bind(this);
    this.throttlePositionDemo = smoothThrottle(this.positionDemo);
  }

  connectedCallback() {
    this.demoName = this.dataset?.demoName;
    this.demoStorageKey = `${this.demoName}-demo-completed`;

    // NOTE: If demo already completed, remove immediately
    if (localStorage.getItem(this.demoStorageKey)) {
      console.log("[InteractiveDemo] Already completed:", this.demoStorageKey);
      this.remove();
      return;
    }

    this.demoStep = this.dataset?.demoStep;
    this.nextDemo = document.querySelector(`[data-demo-step="${this.step + 1}"][data-demo-name="${this.demoName}"]`);
    this.targetId = this.dataset?.targetId;
    this.position = this.dataset?.position ?? "";
    this.mobilePosition = this.dataset?.mobilePosition ?? this.position;
    const {
      buttons: { x, next, cancel },
    } = window.interactiveDemosStrings;

    this.elements = {
      demoCount: null,
      xButton: this.createButton("×", ["fp-demo__x-button"], x.label),
      nextButton: this.createButton(next.text, undefined, next.label),
      cancelButton: this.createButton(cancel.text, undefined, cancel.label),
    };

    this.targetEl = this.targetId ? document.querySelector(`#${this.targetId}`) : null;

    this.elements.xButton.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" fill="none" viewBox="0 0 14 15"><path fill="#191c1f" stroke="#191c1f" stroke-width="1.5" d="M1.234 13.488a.233.233 0 0 1-.165-.399L12.6 1.556a.233.233 0 1 1 .33.33L1.4 13.42a.23.23 0 0 1-.165.069Z"/><path fill="#191c1f" stroke="#191c1f" stroke-width="1.5" d="M12.766 13.488a.23.23 0 0 1-.165-.069L1.07 1.887a.233.233 0 1 1 .33-.33L12.93 13.088a.233.233 0 0 1-.165.399Z"/></svg>';
    this.elements.xButton.addEventListener("click", this.complete);
    this.appendChild(this.elements.xButton);

    if (this.nextDemo) {
      this.elements.nextButton.addEventListener("click", this.next);
      this.appendChild(this.elements.nextButton);

      if (this.step === 1) {
        this.open();
      }
      return;
    }

    this.elements.cancelButton.addEventListener("click", this.complete);
    this.appendChild(this.elements.cancelButton);

    if (this.step === 1) {
      this.open();
    }

    // Reposition demo after Read More / Read Less toggle
    const toggleButtons = document.querySelectorAll(".fp-truncate-text-toggle");
    toggleButtons.forEach((btn) => {
      btn.addEventListener("click", () => {
        requestAnimationFrame(() => {
          document.querySelectorAll("interactive-demo.active").forEach((demo) => {
            demo.positionDemo();
          });
        });
      });
    });
  }

  disconnectedCallback() {
    if (localStorage.getItem(this.demoStorageKey)) {
      console.log("[InteractiveDemo] Cleaning up completed demo:", this.demoStorageKey);
      this.demoName = null;
      this.demoStorageKey = null;
      return;
    }

    this.demoStep = null;
    this.nextDemo = null;
    this.targetId = null;
    this.targetEl = null;
    this.mobilePosition = null;

    // NOTE: Clean up created elements
    Object.values(this.elements).forEach((el) => el?.remove());
    this.elements = null;
  }

  get step() {
    return Number(this.demoStep);
  }

  get demoTarget() {
    return this.targetEl;
  }

  /**
   * Opens the demo step.
   * - Adds "active" state and positions the demo relative to its target element.
   * - Displays step count (e.g., "1 of 3").
   * - Attaches scroll/resize listeners to reposition as needed.
   *
   */
  open() {
    if (this.targetEl?.hasAttribute("hidden")) return;

    this.classList.add("active");
    this.setAttribute("role", "dialog");
    this.setAttribute("aria-live", "polite");

    this.totalSteps = document.querySelectorAll(`[data-demo-name=${this.demoName}]`)?.length ?? 1;
    (this.elements || (this.elements = {})).demoCount = this.createDemoCount();
    this.prepend(this.elements.demoCount);

    document.addEventListener("scroll", this.throttlePositionDemo);
    window.addEventListener("resize", this.throttlePositionDemo);

    this.positionDemo();
  }

  /**
   * Closes the demo step.
   * - Removes "active" state.
   * - Detaches scroll/resize listeners.
   */
  close() {
    this.elements.demoCount?.remove();
    this.classList.remove("active");
    this.removeAttribute("role");
    document.removeEventListener("scroll", this.throttlePositionDemo);
    window.removeEventListener("resize", this.throttlePositionDemo);
  }

  /**
   * Marks the demo as completed.
   * - Stores a localStorage flag to prevent repeat display.
   * - Closes the current demo.
   */
  complete() {
    localStorage.setItem(this.demoStorageKey, "true");
    console.log("[InteractiveDemo] Completing demo:", this.demoStorageKey);
    this.close();
  }

  /**
   * Advances to the next step in the demo sequence.
   * - Closes the current demo.
   * - Scrolls the next target into view.
   * - Opens the next demo step.
   */
  next() {
    this.close();
    this.nextDemo?.demoTarget.scrollIntoView({
      block: "start",
      inline: "nearest",
      behavior: "smooth",
    });
    this.nextDemo?.open();
  }

  createDemoCount() {
    const span = document.createElement("span");
    span.innerText = `${this.step} of ${this.totalSteps}`;
    span.classList.add("body-md");
    span.id = `fp-demo-count`;
    return span;
  }

  createButton(text = "", classes = ["button", "button--tertiary", "fp-demo__button"], label = "") {
    const button = document.createElement("button");
    button.innerText = text;
    button.id = `fp-button-${text.toLowerCase()}`;
    button.setAttribute("aria-label", label || text);

    classes.forEach((cl) => button.classList.add(cl));
    return button;
  }

  positionDemo() {
    if (!this.targetEl) return;

    const PSEUDO_EL_HORIZ_CENTER = 56;
    const PSEUDO_EL_VERT_CENTER = 20;
    const MOBILE_BREAKPOINT = 768;

    const targetRect = this.targetEl.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const isMobile = windowWidth < MOBILE_BREAKPOINT;
    const position = isMobile ? this.mobilePosition : this.position;

    let offsetX, offsetY;
    offsetY = targetRect.bottom + PSEUDO_EL_VERT_CENTER;

    switch (position) {
      case "left":
        offsetX = targetRect.x - (this.offsetWidth - PSEUDO_EL_HORIZ_CENTER - targetRect.width / 2);
        break;
      case "right":
        offsetX = targetRect.x + (PSEUDO_EL_HORIZ_CENTER - targetRect.width / 2);
        break;
      default:
        offsetX = targetRect.x;
        break;
    }

    this.style.setProperty("--demo-offset-y", `${offsetY / 16}rem`);
    this.style.setProperty("--demo-offset-x", `${offsetX / 16}rem`);
  }
}

customElements.define("interactive-demo", InteractiveDemo);
